<template>
  <b-modal id="bcd-request-form" size="xl" @shown="update" @ok="sendBCDRequest">
    <template #modal-title>
      {{ $t('components.bcd_request_form.title') }}
    </template>
    <b-alert :show="error_message !== null" variant="danger" dismissible>
      {{ error_message }}
    </b-alert>
    <b-overlay rounded="sm">
      <b-form>
        <b-form-group :label="$t('global.enumeration.1', { msg: $t('components.bcd_request_form.bcd_usage') })"
                      label-for="bcd_usage">
          <b-form-select v-model="bcd_usage" :options="translated_bcd_usage_types">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-alert show variant="warning" v-if="bcd_usage===2">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="warning" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.bcd_request_form.vpn_request') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-alert show variant="warning" v-if="bcd_usage===3">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="warning" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.bcd_request_form.wifi_request') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group :label="$t('global.enumeration.2', { msg: $tc('system.organizational_unit', 1) })"
                      label-for="oe-select" v-if="bcd_usage === 1 && (oes && oes.length > 0)">
          <Typeahead :data="oes" v-model="oe_search"
                     :placeholder="$tc('system.organizational_unit', 1)"
                     @select="selected_oe=$event;$nextTick(updateGroups)"
                     @input="selected_oe=null;updateGroups()"
                     :serializer="serialize_oe"
                     :min-matching-chars="-1"
                     :max-matches="-1"
                     compact
                     ref="oe-select"
                     class="mb-3" id="oe-select"/>
        </b-form-group>
        <b-form-group v-if="bcd_usage === 1 && selected_oe"
                      label-for="group-select" :description="$t('components.bcd_request_form.group_assignment_note')">
          <template #label>
            {{ $t('global.enumeration.3', { msg: $t('system.netvs_group') }) }}
          </template>
          <Typeahead :data="groups" v-model="group_search"
                     :placeholder="$tc('system.group', 1)"
                     @select="selected_group=$event"
                     @input="selected_group=null"
                     :serializer="serialize_group"
                     :min-matching-chars="-1"
                     :max-matches="-1"
                     :disabled="(groups && groups.length === 0) || loading_edit_action"
                     compact
                     ref="group-select"
                     id="group-select"/>
        </b-form-group>
        <b-alert v-if="bcd_usage === 1 && selected_oe"
                 show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p v-if="!groups || groups.length === 0" class="my-auto">
                {{ $t('components.bcd_request_form.group_assignment_no_group') }}
                <RouterLink to="/cntl/groups" target="_blank">{{ $tc('system.group', 2) }}</RouterLink>
              </p>
              <p v-else class="my-auto">{{ $t('components.bcd_request_form.group_assignment_location') }}
                <RouterLink to="/cntl/groups" target="_blank">{{ $tc('system.group', 2) }}</RouterLink>
              </p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group v-if="selected_group"
                      :description="$t('components.requests.bcd.protection_requirements_note')">
          <template #label>
            {{ $t('global.enumeration.4', { msg: $t('components.requests.bcd.protection_requirements') }) }}
            <a v-if="$store.state.locale !== 'de'" href="https://www.scc.kit.edu/en/services/4325.php" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
            <a v-else href="https://www.scc.kit.edu/dienste/4325.php" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
          </template>
          <b-form-select v-model="protection_requirements_select" :options="translated_protection_requirements">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group
          v-if="selected_group && protection_requirements_select && protection_requirements_select !== 3 && protection_requirements_select !== 4"
          :label="$t('components.requests.bcd.protection_requirements_explanation')">
          <b-form-textarea
            :placeholder="$t('components.requests.all.placeholder_text')"
            v-model="protection_requirements_explanation_text"/>
        </b-form-group>
        <b-form-group
          v-if="protection_requirements_select && (protection_requirements_select === 3 || protection_requirements_select === 4 || (protection_requirements_explanation_text && protection_requirements_explanation_text.length > 0))"
          :label="$t('global.enumeration.5', { msg: $t('components.bcd_request_form.types_of_connected_systems') })"
          :description="$t('components.bcd_request_form.types_of_connected_systems_note')">
          <b-form-select v-model="types_of_systems_select" :options="translated_types_of_systems"
                         @change="types_of_systems_select=$event;$nextTick(updateSystemType)">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group v-if="types_of_systems_select && types_of_systems_select === 6"
                      :label="$t('components.bcd_request_form.other_systems')"
                      :description="$t('components.bcd_request_form.other_systems_note')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="types_of_systems_other_explanation_text"/>
        </b-form-group>
        <b-form-group v-if="types_of_systems_select && types_of_systems_select === 2"
                      :label="$t('global.enumeration.6', { msg: $t('components.bcd_request_form.load_balancing') })">
          <b-form-checkbox v-model="load_balancing_value">{{ $t('components.bcd_request_form.load_balancing_text') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group
          v-if="types_of_systems_select && (types_of_systems_select !== 6 || (types_of_systems_select === 6 && types_of_systems_other_explanation_text && types_of_systems_other_explanation_text.length > 0))"
          :label="$t('global.enumeration.7', { msg: $t('components.requests.bcd.name_of_bcd') })"
          :description="$t('components.requests.bcd.name_of_bcd_description')">
          <b-input-group>
            <b-form-input :placeholder="$t('components.requests.bcd.naming_example_placeholder')"
                          :state="checkBCDName"
                          v-model="bcd_name"/>
            <b-input-group-append>
              <b-button v-b-toggle.guide_collapse variant="outline-secondary">
                <netvs-icon icon="info"></netvs-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-collapse id="guide_collapse">
            <div class="mt-2 mb-2">
              <h5>{{ $t('components.requests.bcd.notes_on_naming') }}</h5>
              <p>{{ $t('components.requests.bcd.naming_scheme_usage') }}:
                <code>{{ $t('components.requests.bcd.naming_scheme') }}</code></p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_oe') }}</code>
                {{ $t('components.requests.bcd.naming_oe_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_division') }}</code>
                {{ $t('components.requests.bcd.naming_division_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_function') }}</code>
                {{ $t('components.requests.bcd.naming_function_description') }}</p>
              <p class="mb-1"><code>{{ $t('components.requests.bcd.naming_number') }}</code>
                {{ $t('components.requests.bcd.naming_number_description') }}</p>
              <p>{{ $t('components.requests.bcd.naming_length') }}</p>
            </div>
          </b-collapse>
        </b-form-group>
        <b-form-group v-if="bcd_name && bcd_name.length > 0"
                      :label="$t('global.enumeration.8', { msg: $t('components.requests.bcd.access_to_internet') })">
          <b-form-checkbox :disabled="!(bcd_name && bcd_name.length > 0) || loading_edit_action"
                           v-model="access_to_internet_value">
            {{ $t('components.requests.bcd.access_to_internet_text') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="bcd_name && bcd_name.length > 0"
                      :label="$t('global.enumeration.9', { msg: $t('components.bcd_request_form.access_from_internet') })"
                      :description="$t('components.bcd_request_form.access_from_internet_note')">
          <b-form-checkbox v-model="access_from_internet_value">
            {{ $t('components.bcd_request_form.access_from_internet_text') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="access_from_internet_value && types_of_systems_select !== 2"
                      :label="$t('components.bcd_request_form.access_from_internet_explanation')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="access_from_internet_explanation_value"/>
        </b-form-group>
        <b-form-group v-if="bcd_name && bcd_name.length > 0"
                      :label="$t('global.enumeration.10', { msg: $t('components.bcd_request_form.ip_address_version') })">
          <b-form-select v-model="types_of_subnets_select" :options="translated_types_of_subnets">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-alert v-if="bcd_name && bcd_name.length > 0" show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.bcd_request_form.ip_address_internal_note') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group v-if="types_of_subnets_select && types_of_subnets_select === 2"
                      :label="$t('global.enumeration.11', { msg: $t('components.bcd_request_form.ipv4_subnet_size') })">
          <b-form-select v-model="subnet_size_select" :options="translated_subnet_sizes">
            <template #first>
              <b-form-select-option :value="null" disabled>{{ $t('system.please_select') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-alert v-if="types_of_subnets_select && types_of_subnets_select === 2" show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.bcd_request_form.ipv4_subnet_size_note') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group
          v-if="types_of_subnets_select && types_of_subnets_select === 2 && subnet_size_select && subnet_size_select === 24"
          :label="$t('components.bcd_request_form.ipv4_subnet_size_explanation')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="subnet_size_explanation_text"/>
        </b-form-group>
        <b-form-group v-if="types_of_subnets_select && types_of_subnets_select === 2"
                      :label="$t('global.enumeration.12', { msg: $t('components.bcd_request_form.dhcp_own') })">
          <b-form-checkbox
            v-model="dhcp_own_server_value"
            :disabled="dhcp_wish_value || loading_edit_action">
            {{ $t('components.bcd_request_form.dhcp_own_question') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_own_server_value"
                      :label="$t('components.bcd_request_form.dhcp_ip_address')">
          <b-form-input v-model="dhcp_own_server_address" :placeholder="$t('components.requests.all.placeholder_text')"
                        :disabled="loading_edit_action || !dhcp_own_server_value"/>
        </b-form-group>
        <b-form-group v-if="types_of_subnets_select && types_of_subnets_select === 2"
                      :description="$t('components.bcd_request_form.dhcp_wish_note')">
          <template #label>
            {{ $t('global.enumeration.13', { msg: $t('system.dhcp') }) }}
            <a href="https://doku.netdb.scc.kit.edu/dhcp/dnsvs/" target="_blank">
              <netvs-icon icon="info"></netvs-icon>
            </a>
          </template>
          <b-form-checkbox
            v-model="dhcp_wish_value"
            :disabled="!types_of_subnets_select || dhcp_own_server_value || !(types_of_subnets_select === 2) || loading_edit_action">
            {{ $t('components.bcd_request_form.dhcp_wish') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_wish_value"
                      :label="$t('global.enumeration.14', { msg: $t('components.bcd_request_form.dhcp_pool') })">
          <b-form-checkbox v-model="dhcp_pool_wish_value">
            {{ $t('components.bcd_request_form.dhcp_pool_wish') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group v-if="dhcp_pool_wish_value"
                      :label="$t('components.bcd_request_form.dhcp_pool_size')"
                      :description="$t('components.bcd_request_form.dhcp_pool_size_note')">
          <b-form-input v-model="dhcp_pool_size_value" type="number" min="2" step="1"
                        :state="checkDHCPPoolSize"
                        :max="getSubnetSize(this.subnet_size_select ?? 29) - 5"/>
          <b-form-invalid-feedback>
            {{ $t('components.bcd_request_form.dhcp_pool_size_error') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group v-if="types_of_subnets_select"
                      :label="$t('global.enumeration.15', { msg: $t('components.bcd_request_form.device_location') })">
          <b-form-radio-group v-model="location" stacked>
            <b-form-radio :value="1" :disabled="types_of_systems_select === 4|| loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.cs') }}
            </b-form-radio>
            <b-form-radio :value="2" :disabled="types_of_systems_select === 4|| loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.cn') }}
            </b-form-radio>
            <b-form-radio :value="3"
                          :disabled="!(types_of_systems_select === 2 || types_of_systems_select === 6) || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.vm_cluster_unknown') }}
            </b-form-radio>
            <b-form-radio :value="4"
                          :disabled="!(types_of_systems_select === 2 || types_of_systems_select === 6) || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.vm_cluster_cs') }}
            </b-form-radio>
            <b-form-radio :value="5"
                          :disabled="!(types_of_systems_select === 2 || types_of_systems_select === 6) || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.vm_cluster_cn') }}
            </b-form-radio>
            <b-form-radio :value="6"
                          :disabled="!(types_of_systems_select === 2 || types_of_systems_select === 6) || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.scc_server_room_cs') }}
            </b-form-radio>
            <b-form-radio :value="7"
                          :disabled="!(types_of_systems_select === 2 || types_of_systems_select === 6) || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.scc_server_room_cn') }}
            </b-form-radio>
            <b-form-radio :value="8"
                          :disabled="types_of_systems_select === 2 || types_of_systems_select === 3 || loading_edit_action">
              {{ $t('components.bcd_request_form.device_locations.iot_only') }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-alert v-if="types_of_subnets_select" show variant="info">
          <b-row>
            <b-col cols="auto" class="my-auto">
              <netvs-icon icon="info" size="2x"></netvs-icon>
            </b-col>
            <b-col class="h-100 my-auto">
              <p class="my-auto">{{ $t('components.bcd_request_form.location_select_note') }}</p>
            </b-col>
          </b-row>
        </b-alert>
        <b-form-group v-if="types_of_subnets_select"
                      :label="$t('components.bcd_request_form.location_special_case')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="location_note"/>
        </b-form-group>
        <b-form-group :label="$t('components.requests.all.additional_recipients')"
                      :description="$t('components.requests.all.additional_recipients_description')"
                      label-for="input_additional_recipients">
          <b-form-input
            :placeholder="$t('components.requests.all.additional_recipients')"
            id="input_additional_recipients"
            v-model="additional_recipients"/>
        </b-form-group>
        <b-form-group :label="$t('components.bcd_request_form.additional_comments')">
          <b-form-textarea :placeholder="$t('components.requests.all.placeholder_text')"
                           v-model="additional_comments_text"/>
        </b-form-group>
      </b-form>
    </b-overlay>
    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="reset">
        {{ $t('components.requests.all.reset_request') }}
      </b-button>
      <b-button variant="secondary" @click="cancel()">
        {{ $t('components.requests.all.cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="location == null || location > 8 || location < 1" @click="ok()">
        {{ $t('components.requests.all.send') }}
        <b-spinner small v-show="sending"/>
        <netvs-icon v-show="status_icon !== null" :icon="status_icon"></netvs-icon>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Typeahead from '@/components/Typeahead.vue'
import OUService from '@/api-services/ou.service'
import ipaddress from '@/util/ipaddress'
import RequestsService from '@/api-services/requests.service'
import translationutil from '@/util/translationutil'

export default {
  name: 'BCDRequestForm',
  components: { Typeahead },
  data() {
    return {
      error_message: null,
      additional_recipients: null,
      bcd_usage: null,
      bcd_usage_type_options: [
        { value: 1, translation_key: 'components.bcd_request_form.bcd_usage_types.network' },
        { value: 2, translation_key: 'components.bcd_request_form.bcd_usage_types.vpn2vlan' },
        { value: 3, translation_key: 'components.bcd_request_form.bcd_usage_types.wifi2vlan' }
      ],
      oes: null,
      oe_search: '',
      selected_oe: null,
      serialize_oe: oe => `${'title' in oe ? translationutil.translate_netdb_ot_lang_attr(oe.title, this.$store.state) : oe.name} (${oe.fq_name})`,
      groups: null,
      group_search: '',
      selected_group: null,
      serialize_group: group => group.name,
      loading_edit_action: false,
      additional_comments_text: null,
      bcd_name: '',
      load_balancing_value: false,
      access_to_internet_value: false,
      access_from_internet_value: false,
      access_from_internet_explanation_value: null,
      dhcp_pool_wish_value: false,
      dhcp_pool_size_value: '2',
      dhcp_own_server_value: false,
      dhcp_own_server_address: null,
      dhcp_wish_value: false,
      location: null,
      location_note: null,
      protection_requirements_select: null,
      protection_requirements_explanation_text: null,
      protection_requirements_options: [
        {
          value: 1,
          translation_key: 'components.requests.bcd.protection_requirements_items.extranet_without_firewall'
        },
        { value: 2, translation_key: 'components.requests.bcd.protection_requirements_items.extranet_with_firewall' },
        { value: 3, translation_key: 'components.requests.bcd.protection_requirements_items.level_1' },
        { value: 4, translation_key: 'components.requests.bcd.protection_requirements_items.level_2' },
        { value: 5, translation_key: 'components.requests.bcd.protection_requirements_items.quarantine_network' },
        { value: 10, translation_key: 'components.requests.bcd.protection_requirements_items.special' }
      ],
      types_of_systems_select: null,
      types_of_systems_other_explanation_text: null,
      types_of_systems_options: [
        { value: 1, translation_key: 'components.bcd_request_form.systems.clients' },
        { value: 2, translation_key: 'components.bcd_request_form.systems.server' },
        { value: 3, translation_key: 'components.bcd_request_form.systems.printer' },
        { value: 4, translation_key: 'components.bcd_request_form.systems.iot_devices' },
        { value: 6, translation_key: 'components.bcd_request_form.other' }
      ],
      types_of_subnets_select: null,
      types_of_subnets_options: [
        { value: 1, translation_key: 'components.bcd_request_form.subnets_types.ipv6_only' },
        { value: 2, translation_key: 'components.bcd_request_form.subnets_types.both' }
      ],
      subnet_size_select: null,
      subnet_size_options: [
        { value: 29, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_29' },
        { value: 28, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_28' },
        { value: 27, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_27' },
        { value: 26, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_26' },
        { value: 25, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_25' },
        { value: 24, translation_key: 'components.bcd_request_form.ipv4_subnet_sizes.size_24' }
      ],
      subnet_size_explanation_text: null,
      sending: false,
      status_icon: null
    }
  },
  computed: {
    checkDHCPPoolSize() {
      return parseInt(this.dhcp_pool_size_value) >= 2 && parseInt(this.dhcp_pool_size_value) <= (this.getSubnetSize(this.subnet_size_select ?? 29) - 5)
    },
    checkBCDName() {
      return this.bcd_name.trim().length > 0 && this.bcd_name.trim().length <= 23
    },
    translated_bcd_usage_types() {
      return this.translateOptions(this.bcd_usage_type_options)
    },
    translated_protection_requirements() {
      return this.translateOptions(this.protection_requirements_options)
    },
    translated_types_of_systems() {
      return this.translateOptions(this.types_of_systems_options)
    },
    translated_types_of_subnets() {
      return this.translateOptions(this.types_of_subnets_options)
    },
    translated_subnet_sizes() {
      return this.translateOptions(this.subnet_size_options)
    }
  },
  methods: {
    getSubnetSize(value) {
      return ipaddress.ipv4_subnet_size(value)
    },
    async update() {
      await this.updateOEs()
    },
    async updateOEs() {
      this.clearOE()
      this.loading++
      const response = await OUService.listOwn(this.$store.state)
      this.oes = response.data.unit_list_superset
      if (this.oes.length === 1) {
        this.selected_oe = this.oes[0]
        this.oe_search = this.serialize_oe(this.selected_oe)
        await this.updateGroups()
      }
      this.loading--
    },
    async updateGroups() {
      this.clearGroup()
      if (!this.selected_oe) return
      this.loading++
      const response = await OUService.getOU(this.$store.state, this.selected_oe.fq_name.toUpperCase())
      this.groups = response.data.group_list
      if (this.groups.length === 1) {
        this.selected_group = this.groups[0]
        this.group_search = this.serialize_group(this.selected_group)
      }
      this.loading--
    },
    async updateSystemType() {
      // Clear the value of the load balancers if the type of system has changed
      this.clearLoadBalancer()
      // Enable access from the internet if server network
      if (this.types_of_systems_select === 2) {
        this.access_from_internet_value = true
      } else {
        this.clearAccessFromInternet()
      }
      this.clearAccessToInternet()
    },
    clear() {
      // Clear everything
      this.clearBCDUsage()
      this.clearOE()
      this.clearGroup()
      this.clearProtectionRequirements()
      this.clearTypeOfSystem()
      this.clearLoadBalancer()
      this.clearBCDName()
      this.clearAccessToInternet()
      this.clearAccessFromInternet()
      this.clearTypeOfSubnet()
      this.clearDHCP()
      this.clearLocations()
      this.clearAdditionalComment()
      this.additional_recipients = null
      // Clear the error message
      this.error_message = null
      // Clear icon
      this.status_icon = null
    },
    clearBCDUsage() {
      this.bcd_usage = null
    },
    clearOE() {
      this.oes = null
      this.selected_oe = null
      this.oe_search = ''
    },
    clearGroup() {
      this.group_search = null
      this.selected_group = null
      this.group_search = ''
    },
    clearLoadBalancer() {
      this.load_balancing_value = false
    },
    clearAccessToInternet() {
      this.access_to_internet_value = false
    },
    clearAccessFromInternet() {
      this.access_from_internet_value = false
      this.access_from_internet_explanation_value = null
    },
    clearProtectionRequirements() {
      this.protection_requirements_select = null
      this.protection_requirements_explanation_text = null
    },
    clearBCDName() {
      this.bcd_name = ''
    },
    clearTypeOfSubnet() {
      this.types_of_subnets_select = null
      this.subnet_size_select = null
      this.subnet_size_explanation_text = null
    },
    clearDHCP() {
      this.dhcp_wish_value = false
      this.dhcp_own_server_value = false
      this.dhcp_own_server_address = null
      this.dhcp_pool_wish_value = false
      this.dhcp_pool_size_value = '2'
    },
    clearTypeOfSystem() {
      this.types_of_systems_select = null
    },
    clearLocations() {
      this.location = null
      this.location_note = null
    },
    clearAdditionalComment() {
      this.additional_comments_text = null
    },
    reset() {
      this.clear()
      this.update()
    },
    translateOptions(items) {
      // Iterate over the list of options
      const copy_items = JSON.parse(JSON.stringify(items))
      for (const copy_element of copy_items) {
        // Check if the item is an object to apply translations to
        if (typeof copy_element === 'object') {
          if (Object.hasOwn(copy_element, 'translation_key')) {
            if (Object.hasOwn(copy_element, 'translation_args')) {
              // Generate the text of the item dynamically
              copy_element.text = this.$t(copy_element.translation_key, copy_element.translation_args)
            } else {
              // Generate the text of the item dynamically
              copy_element.text = this.$t(copy_element.translation_key)
            }
          }
        }
      }
      return copy_items
    },
    sendBCDRequest(e) {
      e.preventDefault()
      this.status_icon = null
      this.sending = true
      this.error_message = null
      let additional_recipients_list = []
      if (this.additional_recipients && this.additional_recipients.trim().length > 0) {
        additional_recipients_list = this.additional_recipients.trim().split(',')
      }
      RequestsService.send_bcd_request(this.$store.state, {
        oe: this.selected_oe.fq_name,
        group: this.selected_group.name,
        protection_requirement: this.protection_requirements_select,
        protection_requirement_note: this.protection_requirements_explanation_text,
        type_of_system: this.types_of_systems_select,
        type_of_system_note: this.types_of_systems_other_explanation_text,
        load_balancer: this.load_balancing_value,
        bcd_name: this.bcd_name,
        access_to_internet: this.access_to_internet_value,
        access_from_internet: this.access_from_internet_value,
        access_from_internet_explanation: this.access_from_internet_explanation_value,
        type_of_subnet: this.types_of_subnets_select,
        ipv4_subnet_size: this.subnet_size_select,
        ipv4_subnet_size_explanation: this.subnet_size_explanation_text,
        dhcp: this.dhcp_wish_value,
        dhcp_pool: this.dhcp_pool_wish_value,
        dhcp_pool_size: this.dhcp_pool_size_value,
        own_dhcp_server: this.dhcp_own_server_value,
        own_dhcp_ip: this.dhcp_own_server_address,
        location: this.location,
        location_note: this.location_note,
        additional_recipients: additional_recipients_list,
        additional_comments: this.additional_comments_text
      }).then(response => {
        this.sending = false
        this.status_icon = 'request_sent'
        this.clear()
        // close modal after 1 second
        setTimeout(() => {
          this.$bvModal.hide('bcd-request-form')
        }, 1000)
      }).catch(error => {
        window.console.log(error)
        this.sending = false
        this.status_icon = 'transaction_error'
        if (error.response.status >= 400 && error.response.status < 500) {
          this.error_message = error.response.data.detail
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
